import React, { useState, useEffect } from 'react';
import { listingsAPI, sellersAPI, storesAPI } from '../api';
import StarRating from './StarRating';

interface ComprehensiveRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseId: number;
  listingId: number;
  listingTitle: string;
  sellerId: number;
  sellerName: string;
  storeId?: number;
  storeName?: string;
  onRated: () => void;
}

const ComprehensiveRatingModal: React.FC<ComprehensiveRatingModalProps> = ({
  isOpen,
  onClose,
  purchaseId,
  listingId,
  listingTitle,
  sellerId,
  sellerName,
  storeId,
  storeName,
  onRated,
}) => {
  // Item rating state
  const [itemRating, setItemRating] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [goodQuality, setGoodQuality] = useState<boolean | null>(null);
  const [valueForPrice, setValueForPrice] = useState<boolean | null>(null);
  const [itemComment, setItemComment] = useState('');

  // Seller rating state
  const [sellerOverallExperience, setSellerOverallExperience] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const [communication, setCommunication] = useState(0);
  const [shippingSpeed, setShippingSpeed] = useState(0);
  const [accurateDescription, setAccurateDescription] = useState(0);
  const [sellerComment, setSellerComment] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemStats, setItemStats] = useState<any>(null);
  const [sellerStats, setSellerStats] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen, listingId, sellerId]);

  const loadStats = async () => {
    try {
      // Load item stats
      const listing = await listingsAPI.getOne(listingId);
      if (listing.item_rating) {
        setItemStats(listing.item_rating);
      }

      // Load seller stats
      const sellerProfile = await sellersAPI.getProfile(sellerId);
      if (sellerProfile?.ratings) {
        setSellerStats(sellerProfile.ratings);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate item rating
    if (itemRating === 0) {
      setError('Please rate the item');
      return;
    }

    // Validate seller rating
    if (!sellerOverallExperience || communication === 0 || shippingSpeed === 0 || accurateDescription === 0) {
      setError('Please complete the seller rating section');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Submit item rating
      await listingsAPI.rateItem(purchaseId, listingId, {
        rating: itemRating,
        would_recommend: wouldRecommend,
        good_quality: goodQuality,
        value_for_price: valueForPrice,
        comment: itemComment,
      });

      // Use average of detailed ratings for star display; overall experience only for positive/neutral/negative bucket
      const numericRating = Math.round((accurateDescription + shippingSpeed + communication) / 3);

      // Submit seller rating
      if (storeId && storeName) {
        await storesAPI.rateStore(storeId, purchaseId, 
          numericRating,
          sellerComment || undefined, // Optional comment for seller rating
          accurateDescription,
          shippingSpeed,
          communication,
          sellerOverallExperience as 'positive' | 'neutral' | 'negative'
        );
      } else {
        await sellersAPI.rateSeller(sellerId, purchaseId,
          numericRating,
          sellerComment || undefined, // Optional comment for seller rating
          accurateDescription,
          shippingSpeed,
          communication,
          sellerOverallExperience as 'positive' | 'neutral' | 'negative'
        );
      }

      onRated();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit ratings');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setItemRating(0);
    setWouldRecommend(null);
    setGoodQuality(null);
    setValueForPrice(null);
    setItemComment('');
    setSellerOverallExperience(null);
    setCommunication(0);
    setShippingSpeed(0);
    setAccurateDescription(0);
    setSellerComment('');
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Rate Your Purchase</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Rate the Item Section */}
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="text-xl font-bold text-blue-900 mb-4">Rate the item</h3>
            
            {/* Item Name and Overall Rating */}
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-lg font-semibold text-gray-900">{listingTitle}</span>
                {itemStats && (
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-400">⭐</span>
                    <span className="font-semibold">{itemStats.average_rating?.toFixed(2) || '0.00'}</span>
                    <span className="text-gray-600 text-sm">({itemStats.total_ratings || 0} reviews)</span>
                  </div>
                )}
              </div>
              
              {/* Key Metrics */}
              {itemStats && (
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <div className="flex items-center gap-1 text-green-700">
                    <i className="ri-check-line"></i>
                    <span>{Math.round((itemStats.would_recommend_percentage || 0) * 10) / 10}% would recommend</span>
                  </div>
                  <div className="flex items-center gap-1 text-green-700">
                    <i className="ri-check-line"></i>
                    <span>{Math.round((itemStats.value_for_price_percentage || 0) * 10) / 10}% good value</span>
                  </div>
                  <div className="flex items-center gap-1 text-green-700">
                    <i className="ri-check-line"></i>
                    <span>{Math.round((itemStats.good_quality_percentage || 0) * 10) / 10}% good quality</span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-blue-300 pt-4 space-y-4">
              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overall Rating *
                </label>
                <StarRating
                  rating={itemRating}
                  onRatingChange={setItemRating}
                  size="lg"
                  allowHalfStars={true}
                />
              </div>

              {/* Boolean Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wouldRecommend === true}
                    onChange={(e) => setWouldRecommend(e.target.checked ? true : null)}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Would recommend</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={goodQuality === true}
                    onChange={(e) => setGoodQuality(e.target.checked ? true : null)}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Good quality / As described</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={valueForPrice === true}
                    onChange={(e) => setValueForPrice(e.target.checked ? true : null)}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Value for price</span>
                </label>
              </div>

              {/* Optional Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optional Comment
                </label>
                <textarea
                  value={itemComment}
                  onChange={(e) => setItemComment(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share your thoughts about this item..."
                />
              </div>
            </div>
          </div>

          {/* Rate the Seller Section */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Rate the seller</h3>
            
            {/* Seller Feedback Summary */}
            {sellerStats && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-lg">Seller Feedback:</span>
                  <span className="text-2xl">👍</span>
                  <span className="font-semibold text-gray-900">
                    {sellerStats.positive_percentage?.toFixed(0) || 0}% Buyer Approved
                  </span>
                  <span className="text-gray-600 text-sm">
                    ({sellerStats.total || 0} ratings)
                  </span>
                </div>
              </div>
            )}

            <div className="border-t border-gray-300 pt-4 space-y-4">
              {/* Overall Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Overall Experience *
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setSellerOverallExperience('positive')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition ${
                      sellerOverallExperience === 'positive'
                        ? 'bg-green-500 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-2xl">👍</span>
                      <span className="font-medium">Approved</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSellerOverallExperience('neutral')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition ${
                      sellerOverallExperience === 'neutral'
                        ? 'bg-yellow-500 text-white border-yellow-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-yellow-400'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-2xl">😐</span>
                      <span className="font-medium">Neutral</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSellerOverallExperience('negative')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition ${
                      sellerOverallExperience === 'negative'
                        ? 'bg-red-500 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-2xl">👎</span>
                      <span className="font-medium">Not approved</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Detailed Seller Ratings */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-700">Detailed Seller Ratings:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-semibold text-gray-900">{storeName || sellerName}</span>
                    {storeName && <i className="ri-store-line text-gray-500"></i>}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Communication *
                    </label>
                    <StarRating
                      rating={communication}
                      onRatingChange={setCommunication}
                      size="md"
                      allowHalfStars={true}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shipping Speed *
                    </label>
                    <StarRating
                      rating={shippingSpeed}
                      onRatingChange={setShippingSpeed}
                      size="md"
                      allowHalfStars={true}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accurate Description *
                    </label>
                    <StarRating
                      rating={accurateDescription}
                      onRatingChange={setAccurateDescription}
                      size="md"
                      allowHalfStars={true}
                    />
                  </div>
                </div>
              </div>

              {/* Optional Comment for Seller Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optional Comment
                </label>
                <textarea
                  value={sellerComment}
                  onChange={(e) => setSellerComment(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Share your thoughts about this seller..."
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="flex space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || itemRating === 0 || !sellerOverallExperience || communication === 0 || shippingSpeed === 0 || accurateDescription === 0}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComprehensiveRatingModal;

